import { CONTENT_BLOCK_KINDS } from './types.js';
/** monday's live counters: 255 for text/name, 2000 for long text `[discovery/forms/README.md]`. */
export const DEFAULT_TEXT_LIMIT = 255;
export const DEFAULT_LONG_TEXT_LIMIT = 2000;
export function characterLimitFor(question) {
    const explicit = question.settings.limit;
    if (typeof explicit === 'number' && explicit > 0)
        return explicit;
    if (question.kind === 'long_text')
        return DEFAULT_LONG_TEXT_LIMIT;
    if (question.kind === 'short_text' || question.kind === 'name')
        return DEFAULT_TEXT_LIMIT;
    return null;
}
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2})?)?$/;
function isBlank(value) {
    if (value === undefined || value === null)
        return true;
    if (typeof value === 'string')
        return value.trim() === '';
    if (Array.isArray(value))
        return value.length === 0;
    if (typeof value === 'object')
        return Object.keys(value).length === 0;
    // `false` is a real answer to a checkbox, but it is not an answer to "required".
    if (typeof value === 'boolean')
        return value === false;
    return false;
}
/**
 * Which questions are actually on screen: a question hidden by its `showIfRule` cannot be
 * required, and neither can an invisible one or a content block.
 */
export function visibleQuestions(questions, settings, answers) {
    const rulesById = new Map(settings.conditionalRules.map((rule) => [rule.id, rule]));
    return questions.filter((question) => {
        if (!question.visible)
            return false;
        if (question.showIfRuleId === null)
            return true;
        const rule = rulesById.get(question.showIfRuleId);
        if (rule === undefined)
            return false;
        const answer = answers[rule.questionId];
        const given = Array.isArray(answer) ? answer.map(String) : answer === undefined || answer === null ? [] : [String(answer)];
        return given.some((value) => rule.values.includes(value));
    });
}
function checkOne(question, value) {
    if (isBlank(value))
        return null;
    const limit = characterLimitFor(question);
    if (limit !== null && typeof value === 'string' && value.length > limit)
        return 'too_long';
    switch (question.kind) {
        case 'number':
        case 'rating': {
            const numeric = typeof value === 'number' ? value : Number(String(value));
            if (!Number.isFinite(numeric))
                return question.kind === 'rating' ? 'invalid_rating' : 'not_a_number';
            if (question.kind === 'rating' && (numeric < 0 || numeric > 10))
                return 'invalid_rating';
            return null;
        }
        case 'email':
            return typeof value === 'string' && EMAIL_RE.test(value.trim()) ? null : 'invalid_email';
        case 'link': {
            const text = String(value).trim();
            return /^https?:\/\/\S+$/.test(text) ? null : 'invalid_url';
        }
        case 'date':
            return typeof value === 'string' && DATE_RE.test(value.trim()) ? null : 'invalid_date';
        case 'date_range': {
            if (!Array.isArray(value) && typeof value !== 'object')
                return 'invalid_date';
            const range = Array.isArray(value)
                ? { from: value[0], to: value[1] }
                : value;
            for (const part of [range.from, range.to]) {
                if (part === undefined || part === null || part === '')
                    continue;
                if (typeof part !== 'string' || !DATE_RE.test(part.trim()))
                    return 'invalid_date';
            }
            return null;
        }
        case 'single_select': {
            if (question.options.length === 0)
                return null;
            return question.options.some((option) => option.value === String(value)) ? null : 'invalid_option';
        }
        case 'multi_select': {
            if (question.options.length === 0)
                return null;
            const values = Array.isArray(value) ? value.map(String) : [String(value)];
            return values.every((v) => question.options.some((option) => option.value === v)) ? null : 'invalid_option';
        }
        default:
            return null;
    }
}
export function validateFormAnswers(questions, settings, answers) {
    const errors = {};
    for (const question of visibleQuestions(questions, settings, answers)) {
        if (CONTENT_BLOCK_KINDS.has(question.kind))
            continue;
        const value = answers[question.id];
        if (question.required && isBlank(value)) {
            errors[question.id] = 'required';
            continue;
        }
        const failure = checkOne(question, value);
        if (failure !== null)
            errors[question.id] = failure;
    }
    return { ok: Object.keys(errors).length === 0, errors };
}
/**
 * The column value an answer becomes, in the shape `upsertCellValue` expects for that column
 * type. `null` means "this kind writes no cell" (a content block, or an updates question);
 * `undefined` means "we have no writer for this column type yet" — the caller parks the answer
 * in the submission payload instead of guessing (q297).
 */
export function answerToColumnValue(kind, columnType, value) {
    if (CONTENT_BLOCK_KINDS.has(kind) || kind === 'updates' || kind === 'name')
        return null;
    if (columnType === undefined)
        return undefined;
    if (isBlank(value) && typeof value !== 'boolean')
        return null;
    switch (columnType) {
        case 'text':
        case 'long_text':
            return String(value);
        case 'numbers':
            return typeof value === 'number' ? value : Number(String(value));
        case 'checkbox':
            return { checked: value === true || value === 'true' };
        case 'date': {
            const text = String(value);
            const [date, time] = text.split(/[T ]/);
            return time ? { date, time: time.slice(0, 8) } : { date };
        }
        case 'status':
        case 'color_picker':
            return { index: Number(String(value)) };
        case 'dropdown':
            return { ids: (Array.isArray(value) ? value : [value]).map((v) => Number(String(v))) };
        case 'people':
        case 'person':
            return {
                personsAndTeams: (Array.isArray(value) ? value : [value]).map((v) => ({
                    id: Number(String(v)),
                    kind: 'person',
                })),
            };
        default:
            return undefined;
    }
}
//# sourceMappingURL=validate.js.map